import React, { useState, useMemo, memo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Archive, CheckCircle, Info, X, History, ChevronDown, ChevronUp, Plus, Equal, BadgePercent, RotateCcw, StickyNote, Coins } from 'lucide-react'; // Removed Truck, Gift
import { Order } from '@/supabase/order/types/order';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RelevantOrdersPanelProps {
  isOpen: boolean;
  isLoading: boolean;
  orders: Order[];
  nickname: string;
  orderId?: string;
  // memberSetting: MemberSetting; // Removed as it's unused
  currentOrderTotal: number;
  shippingFeeOverride: number | null;
  setShippingFeeOverride: (value: number | null) => void;
  onClose: () => void;
  hasProducts: boolean;
}

const statusConfig = {
  '미지정': { icon: Info, color: 'text-gray-500' },
  '부분 입금': { icon: Coins, color: 'text-amber-500' },
  '입금 완료': { icon: CheckCircle, color: 'text-green-600' },
  '출고 완료': { icon: Archive, color: 'text-blue-600' },
};

const OrderItemRow: React.FC<{ item: NonNullable<Order['items']>[0] }> = memo(({ item }) => (
  <div className="flex justify-between items-center text-sm text-gray-600 py-1.5 px-2">
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex-1 truncate pr-2">{item.item_name}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{item.item_name}</p>
      </TooltipContent>
    </Tooltip>
    <span className="w-16 text-right">{item.price.toLocaleString()}원</span>
    <span className="w-12 text-right">{item.quantity}개</span>
  </div>
));

const OrderCard: React.FC<{ order: Order }> = memo(({ order }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const StatusIcon = statusConfig[order.status]?.icon || Info;
  const statusColor = statusConfig[order.status]?.color || 'text-gray-500';

  return (
    <li className="bg-white rounded-lg border border-gray-200/80 transition-shadow hover:shadow-md">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left p-3"
        aria-expanded={isExpanded}
      >
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="font-semibold text-gray-800 text-sm flex items-center">
              {format(new Date(order.order_date), 'yyyy. MM. dd')}
              {order.memo && <StickyNote className="w-3.5 h-3.5 ml-1.5 text-amber-600" />}
            </span>
            <span className="text-xs font-medium text-indigo-600 mt-1">{(order.total_price || 0).toLocaleString()}원</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            {order.is_keep && (
              <span className="font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">
                킵
              </span>
            )}
            <span className={cn("font-semibold flex items-center", statusColor)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {order.status}
            </span>
            <ChevronDown
              className={cn("w-4 h-4 text-gray-400 transition-transform", isExpanded && "rotate-180")}
            />
          </div>
        </div>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t mx-3 my-1.5"></div>
            <div className="px-3 pb-3 space-y-2">
              {order.memo && (
                <div className="mt-2 p-2 bg-amber-50 border-l-4 border-amber-400 rounded-r-md">
                  <p className="text-xs text-amber-900 whitespace-pre-wrap break-words">{order.memo}</p>
                </div>
              )}
              {order.items && order.items.length > 0 ? (
                <div className="space-y-1">
                  {order.items.map(item => <OrderItemRow key={item.id} item={item} />)}
                </div>
              ) : (
                <div className="text-center text-xs text-gray-500 py-2">상품 정보가 없습니다.</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
});

export const RelevantOrdersPanel: React.FC<RelevantOrdersPanelProps> = memo(({
  isOpen,
  isLoading,
  orders,
  nickname,
  orderId,
  // memberSetting, // Removed
  currentOrderTotal,
  shippingFeeOverride,
  setShippingFeeOverride,
  onClose,
  hasProducts,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const hasRelevantOrders = orders.length > 0;
  const isOverridden = shippingFeeOverride === 0;

  const newLocalStorageKey = useMemo(() => (orderId ? `shippingFeeOverride-${orderId}` : null), [orderId]);
  // legacy key 제거됨 (nickname 기반 저장은 더 이상 사용하지 않음)

  // LocalStorage → DB 1회 승격 로직 (레거시 키 제거됨)
  // - 주문이 있는 경우(orderId 존재) AND DB 값이 아직 null일 때만 신규 키의 0을 승격
  // - 승격 후 신규 키 제거
  // - 주문이 없는 경우(세션 고객)는 localStorage 사용 안 함
  useEffect(() => {
    const hasOrderId = !!orderId;
    if (hasOrderId) {
      if (shippingFeeOverride === null) {
        // 신규 키 확인
        if (newLocalStorageKey) {
          const newValue = localStorage.getItem(newLocalStorageKey);
          if (newValue !== null && JSON.parse(newValue) === 0) {
            setShippingFeeOverride(0); // 부모(Home)에서 DB로 저장하도록 위임
            localStorage.removeItem(newLocalStorageKey);
            return;
          }
        }
      }
      // 주문이 있는 경우에는 이후 localStorage를 사용하지 않음
      return;
    }

    // 주문이 없는 세션 고객: localStorage 사용 안 함
  }, [orderId, shippingFeeOverride, newLocalStorageKey, setShippingFeeOverride]);

  // Save or clear shippingFeeOverride in localStorage.
  useEffect(() => {
    // 더 이상 localStorage에 저장하지 않음 (레거시/세션 보관 중단)
    return;
  }, [orderId, shippingFeeOverride, newLocalStorageKey]);

  useEffect(() => {
    if (isOpen) {
      setIsCollapsed(true);
    }
  }, [isOpen]);

  const pastOrdersTotal = useMemo(() => {
    return orders.reduce((sum, order) => sum + (order.total_price || 0), 0);
  }, [orders]);

  const combinedTotal = pastOrdersTotal + currentOrderTotal;

  const handleToggleOverride = () => {
    if (isOverridden) {
      setShippingFeeOverride(null); // Cancel the override
    } else {
      setShippingFeeOverride(0); // Apply the override
    }
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  return (
    <TooltipProvider>
      <AnimatePresence>
        {isOpen && hasRelevantOrders && (
          <motion.div
            key="panel"
            id="relevant-orders-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed bottom-5 right-5 w-full max-w-sm bg-gray-50/80 backdrop-blur-sm rounded-xl border border-gray-200/80 shadow-2xl z-50 flex flex-col"
            style={{ right: `var(--relevant-panel-right, 1.25rem)` }}
          >
            {/* Panel Header */}
            <header className="flex items-center justify-between p-3 border-b border-gray-200/80 flex-shrink-0">
              <h2 id="panel-title" className="text-base font-semibold text-gray-800 flex items-center">
                <History className="w-4 h-4 mr-2 text-indigo-600" />
                <span>
                  <span className="font-bold text-indigo-700">{nickname}</span>
                  <span className="font-medium text-gray-600"> 님의 주문 내역</span>
                </span>
              </h2>
              <div className="flex items-center">
                {hasRelevantOrders && (
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                    aria-label={isCollapsed ? "패널 펼치기" : "패널 접기"}
                  >
                    {isCollapsed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1 ml-1 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                  aria-label="패널 닫기"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>

            {isLoading ? (
              <div className="flex items-center justify-center h-full text-gray-500 py-10 px-4">
                <p>주문 내역을 불러오는 중...</p>
              </div>
            ) : !hasRelevantOrders && !isOverridden ? (
              <div className="p-4 text-center text-sm text-gray-600 border-t border-gray-200/80">
                <p>선택된 날짜 기준 전후 30일 내에 '킵' 또는 '입금 완료' 상태인 다른 주문이 없습니다.</p>
              </div>
            ) : !hasRelevantOrders && isOverridden ? (
              <div className="p-4 text-center text-sm text-amber-800 bg-amber-50 border-t border-gray-200/80">
                <p className="font-semibold">기존 배송비 0원 설정이 적용되어 있습니다.</p>
                <p className="text-xs mt-1">아래 버튼으로 이 설정을 해제할 수 있습니다.</p>
              </div>
            ) : (
              <>
                {/* Filter Info Badge */}
                {/* Filter Info Badge - Conditionally render based on isCollapsed */}
                {/* This badge is only shown when the panel is NOT collapsed */}
                {!isCollapsed && (
                  <div className="px-3 pt-2">
                    <div className="bg-indigo-100 text-indigo-800 text-xs font-medium px-3 py-1.5 rounded-lg flex items-center">
                      <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>선택된 날짜 기준 전후 30일 내의 '킵' 또는 '입금 완료' 주문만 표시됩니다.</span>
                    </div>
                  </div>
                )}

                {/* Panel Content */}
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="overflow-y-auto p-3 max-h-[40vh]">
                        <ul className="space-y-2">
                          {orders.map((order) => <OrderCard key={order.id} order={order} />)}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
            
            {/* Footer Summary */}
            {(!isLoading && (hasRelevantOrders || isOverridden)) && (
              <footer className="p-3 border-t border-gray-200/80 bg-white/50 text-xs">
                {hasRelevantOrders && (
                  <>
                    {isCollapsed && (
                      <div className="text-center text-[#15B8A6] font-semibold mb-2 text-xs">
                        <span>전후 30일 내에 합배송 가능한 다른 주문이 존재합니다.</span>
                      </div>
                    )}
                    {!isCollapsed && (
                      <div className="space-y-2 mb-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">다른 주문들의 총 금액:</span>
                          <span className="font-semibold text-gray-800">{pastOrdersTotal.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium flex items-center"><Plus className="w-3 h-3 mr-1"/>현재 주문 금액:</span>
                          <span className="font-semibold text-gray-800">{currentOrderTotal.toLocaleString()}원</span>
                        </div>
                        <div className="border-t border-dashed my-1"></div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-bold text-gray-800 flex items-center"><Equal className="w-3 h-3 mr-1"/>총 합산 금액:</span>
                          <span className="font-bold text-indigo-600">{combinedTotal.toLocaleString()}원</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {!hasProducts && !isOverridden && (
                  <div className="text-[11px] text-gray-500 text-center mt-1">
                    상품이 추가된 상태에서만 배송비 0원 적용이 가능합니다.
                  </div>
                )}
                <button
                  onClick={handleToggleOverride}
                  disabled={!hasProducts && !isOverridden}
                  className={cn(
                    "w-full mt-2 text-center font-semibold py-2 rounded-md transition-colors text-xs flex items-center justify-center",
                    isOverridden
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "bg-yellow-400 text-yellow-900 hover:bg-yellow-500",
                    !hasProducts && !isOverridden && "bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300"
                  )}
                >
                  {isOverridden ? (
                    <><RotateCcw className="w-4 h-4 mr-1.5" /> 배송비 0원 적용 취소</>
                  ) : (
                    <><BadgePercent className="w-4 h-4 mr-1.5" /> 현재 정산서 메시지 배송비 0원 적용하기</>
                  )}
                </button>
              </footer>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </TooltipProvider>
  );
});